import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import { Contact, NotificationChannel, Subscription, Topic } from '@/core/notifications/model';
import { describeTagFilter } from '@/core/notifications/model/subscription-coverage';
import ContactOverrideSelect from '@/core/notifications/components/settings/contact-override-select';
import { Loading, CheckboxRow } from '@/shared/components/export';
import { useOpenWidget } from '@/core/widget-navigation/export';
import FieldValuesSummary from './field-values-summary';

function extractErrorMessage(error: any): string {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join('. ');
  if (typeof message === 'string') return message;
  return 'Validation failed';
}

function FilterSummary({ topic, tagFilter }: { topic: Topic; tagFilter: Record<string, unknown> }) {
  const entries = describeTagFilter(topic, tagFilter);

  return (
    <div className="mb-2" style={{ fontSize: 13 }}>
      {entries.length === 0 ? (
        'Matches all events for this topic'
      ) : (
        <FieldValuesSummary entries={entries} />
      )}
    </div>
  );
}

function UnsubscribeConfirm({
  subscription,
  topic,
  alreadyPaused,
  onConfirm,
  onCancel,
  saving,
}: {
  subscription: Subscription;
  topic: Topic;
  alreadyPaused: boolean;
  onConfirm: (action: 'pause' | 'delete') => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [action, setAction] = useState<'pause' | 'delete'>(alreadyPaused ? 'delete' : 'pause');

  return (
    <div className="mt-3 p-3 rounded" style={{ border: '1px solid currentColor' }}>
      <div className="mb-1" style={{ fontWeight: 600 }}>
        {subscription.name}
      </div>
      <FilterSummary topic={topic} tagFilter={subscription.tagFilter} />

      {alreadyPaused ? (
        <div className="mb-2" style={{ fontSize: 13 }}>
          This alarm is already paused. This will permanently delete it.
        </div>
      ) : (
        <>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="radio"
              id="unsubscribe-action-pause"
              checked={action === 'pause'}
              onChange={() => setAction('pause')}
            />
            <label className="form-check-label" htmlFor="unsubscribe-action-pause">
              Pause — stop deliveries but keep this alarm's settings
            </label>
          </div>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="radio"
              id="unsubscribe-action-delete"
              checked={action === 'delete'}
              onChange={() => setAction('delete')}
            />
            <label className="form-check-label" htmlFor="unsubscribe-action-delete">
              Delete — permanently remove this alarm
            </label>
          </div>
        </>
      )}

      <div className="d-flex justify-content-end gap-2">
        <button className="btn btn-sm btn-outline-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          className="btn btn-sm btn-tertiary text-base-light"
          onClick={() => onConfirm(action)}
          disabled={saving}
        >
          {saving ? 'Working…' : 'Confirm'}
        </button>
      </div>
    </div>
  );
}

export default function SubscribeManageView({
  subscription,
  topic,
  channels,
  onUpdated,
  onCancel,
  onBack,
}: {
  subscription: Subscription;
  topic: Topic;
  channels: NotificationChannel[];
  onUpdated: () => void;
  onCancel: () => void;
  onBack?: () => void;
}) {
  const [selectedChannels, setSelectedChannels] = useState<string[]>(() => {
    const base = subscription.defaultChannels;
    return base.includes('in-app') ? base : ['in-app', ...base];
  });
  const [overrides, setOverrides] = useState<Record<string, Record<string, unknown>>>(
    subscription.channelOverrides,
  );
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmingUnsubscribe, setConfirmingUnsubscribe] = useState(false);
  const openWidget = useOpenWidget();

  useEffect(() => {
    notificationApi
      .getContacts()
      .then(setContacts)
      .catch(() => {})
      .finally(() => setContactsLoaded(true));
  }, []);

  const hasVerifiedContact = (channelKey: string) =>
    contacts.some(contact => contact.channelKey === channelKey && contact.isVerified);

  const toggleChannel = (channelKey: string) => {
    setSelectedChannels(prev =>
      prev.includes(channelKey)
        ? prev.filter(existing => existing !== channelKey)
        : [...prev, channelKey],
    );
  };

  const buildChannelOverrides = (): Record<string, Record<string, unknown>> => {
    const channelOverrides: Record<string, Record<string, unknown>> = {};
    selectedChannels.forEach(channelKey => {
      const override = overrides[channelKey];
      if (override && Object.keys(override).length > 0) channelOverrides[channelKey] = override;
    });
    return channelOverrides;
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage(null);
    try {
      await notificationApi.updateSubscription(subscription.id, {
        defaultChannels: selectedChannels,
        channelOverrides: buildChannelOverrides(),
      });
      toast.success('Alarm updated');
      onUpdated();
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeConfirm = async (action: 'pause' | 'delete') => {
    setSaving(true);
    setErrorMessage(null);
    try {
      if (action === 'delete') {
        await notificationApi.deleteSubscription(subscription.id);
        toast.success('Alarm deleted');
      } else {
        await notificationApi.updateSubscription(subscription.id, { paused: true });
        toast.success('Alarm paused');
      }
      onUpdated();
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleResume = async () => {
    setSaving(true);
    setErrorMessage(null);
    try {
      await notificationApi.updateSubscription(subscription.id, { paused: false });
      toast.success('Alarm resumed');
      onUpdated();
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!contactsLoaded) return <Loading type="bar" />;

  return (
    <div>
      {onBack && !confirmingUnsubscribe && (
        <button type="button" className="btn btn-link btn-sm p-0 mb-2" onClick={onBack}>
          ← Back to list
        </button>
      )}
      <div className="mb-1" style={{ fontWeight: 600 }}>
        {subscription.name}
      </div>
      <FilterSummary topic={topic} tagFilter={subscription.tagFilter} />

      {subscription.paused && !confirmingUnsubscribe && (
        <div
          className="mb-3 p-2 rounded d-flex justify-content-between align-items-center"
          style={{
            background: 'rgba(var(--bs-warning-rgb), 0.15)',
            border: '1px solid var(--bs-warning)',
          }}
        >
          <span style={{ fontSize: 13 }}>This alarm is paused — nothing will be delivered.</span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handleResume}
            disabled={saving}
          >
            Resume
          </button>
        </div>
      )}

      {!confirmingUnsubscribe && (
        <div className="mb-3">
          <label className="form-label">Delivery channels</label>
          {channels.map(channel => {
            const disabled = channel.key !== 'in-app' && !hasVerifiedContact(channel.key);
            return (
              <div key={channel.key} className="mb-2">
                <CheckboxRow
                  checked={channel.key === 'in-app' || selectedChannels.includes(channel.key)}
                  disabled={channel.key === 'in-app' || disabled}
                  onChange={() => channel.key !== 'in-app' && toggleChannel(channel.key)}
                  label={channel.displayName}
                >
                  {channel.key === 'in-app' && (
                    <span className="mutedText">— always delivered to this app</span>
                  )}
                </CheckboxRow>
                {disabled && (
                  <div style={{ fontSize: 12, color: 'var(--bs-warning)' }} className="ms-4">
                    No verified contact for this channel —{' '}
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline text-decoration-none"
                      style={{ fontSize: 12 }}
                      onClick={() => openWidget('settings', 'notifications/contacts')}
                    >
                      add one in Notification Settings
                    </button>
                    .
                  </div>
                )}
                {channel.key !== 'in-app' &&
                  selectedChannels.includes(channel.key) &&
                  !disabled && (
                    <div className="ms-4 mt-1">
                      <ContactOverrideSelect
                        channel={channel}
                        contacts={contacts}
                        size="sm"
                        value={overrides[channel.key]}
                        onChange={value =>
                          setOverrides(prev => ({ ...prev, [channel.key]: value }))
                        }
                      />
                      <div className="form-text">
                        Deliveries go to your default contact unless you pick another verified
                        address.
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}

      {errorMessage && (
        <div
          className="px-3 py-2 rounded mb-3"
          style={{
            background: 'rgba(var(--bs-danger-rgb), 0.15)',
            border: '1px solid var(--bs-danger)',
            color: 'var(--bs-danger)',
            fontSize: 13,
          }}
        >
          {errorMessage}
        </div>
      )}

      {confirmingUnsubscribe ? (
        <UnsubscribeConfirm
          subscription={subscription}
          topic={topic}
          alreadyPaused={subscription.paused}
          saving={saving}
          onCancel={() => setConfirmingUnsubscribe(false)}
          onConfirm={handleUnsubscribeConfirm}
        />
      ) : (
        <div className="d-flex justify-content-between">
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => setConfirmingUnsubscribe(true)}
            disabled={saving}
          >
            Unsubscribe
          </button>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              className="btn btn-sm btn-tertiary text-base-light"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
